/**
 * Socket.IO Service for Performance Agent Chat
 * Handles real-time communication with backend
 */

import { io, Socket } from "socket.io-client";
import { v4 as uuidv4 } from "uuid";

// Types
export interface ChatMessage {
  message: string;
  session_id: string;
  user_id: string;
  tenant_id: string;
  message_id?: string;
  context?: Record<string, any>;
}

export interface MessageChunk {
  session_id: string;
  chunk: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface MessageComplete {
  session_id: string;
  message: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface StatusUpdate {
  session_id: string;
  status: string;
  details?: Record<string, any>;
  timestamp: string;
}

export interface ToolExecution {
  session_id: string;
  tool_name: string;
  status: "started" | "success" | "error";
  result?: Record<string, any>;
  timestamp: string;
}

export interface ErrorMessage {
  session_id: string;
  error: string;
  error_type: string;
  timestamp: string;
}

// NEW: Visualization update for Performance Agent
export interface VisualizationUpdate {
  session_id: string;
  type: 'channel_recommendations' | 'audience_value_table' | 'experiment_setup' | 'activation_progress' | 'step_progress';
  step?: number;
  data: Record<string, any>;
  timestamp: string;
}

// NEW: Step progress update
export interface StepProgressUpdate {
  session_id: string;
  current_step: number;
  total_steps: number;
  step_name: string;
  status: 'in_progress' | 'completed';
  timestamp: string;
}

// NEW: Request user input for interactive workflow
export interface RequestUserInput {
  session_id: string;
  step: number;
  input_type: string;  // e.g., 'step1_kpi_selection', 'step1_date_input', 'step1_plan_approval'
  input_data: {
    message: string;
    input_fields?: Record<string, any>;  // Field definitions for form inputs
    actions?: string[];  // Available actions (e.g., ['approve', 'reject'])
    recommendations?: any;  // Data to display (for approval steps)
    segments?: any;
    experiment_config?: any;
    validation_message?: string;
  };
  timestamp: string;
}

// Event handlers type
export interface SocketEventHandlers {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onMessageChunk?: (data: MessageChunk) => void;
  onMessageComplete?: (data: MessageComplete) => void;
  onStatusUpdate?: (status: StatusUpdate) => void;
  onToolExecution?: (tool: ToolExecution) => void;
  onError?: (error: ErrorMessage) => void;
  onTyping?: (data: { session_id: string; is_typing: boolean; user_id: string }) => void;
  // NEW: Performance Agent visualization handlers
  onVisualizationUpdate?: (data: VisualizationUpdate) => void;
  onStepProgress?: (data: StepProgressUpdate) => void;
  onRequestUserInput?: (data: RequestUserInput) => void;  // NEW: Interactive workflow
}

class SocketService {
  private socket: Socket | null = null;
  private sessionId: string | null = null;
  private userId: string | null = null;
  private tenantId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Initialize Socket.IO connection
   */
  connect(
    userId: string,
    tenantId: string,
    backendUrl: string = process.env.REACT_APP_API_URL ||
      "http://localhost:7000"
  ): void {
    if (this.socket?.connected) {
      console.log("Socket already connected");
      return;
    }

    this.userId = userId;
    this.tenantId = tenantId;
    this.sessionId = uuidv4();

    // Get JWT token from localStorage for authentication
    const token = localStorage.getItem("authToken");

    console.log("🔌 Connecting to Socket.IO server...", {
      url: backendUrl,
      userId,
      tenantId,
      sessionId: this.sessionId,
      hasToken: !!token,
    });

    this.socket = io(backendUrl, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: {
        token: token,  // JWT token for server-side authentication
        user_id: userId,
        tenant_id: tenantId,
      },
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    this.setupDefaultHandlers();
  }

  /**
   * Setup default event handlers
   */
  private setupDefaultHandlers(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("✅ Socket.IO connected:", this.socket?.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on("disconnect", (reason: any) => {
      console.log("❌ Socket.IO disconnected:", reason);
      if (reason === "io server disconnect") {
        // Server disconnected, manually reconnect
        this.socket?.connect();
      }
    });

    this.socket.on("connect_error", (error: any) => {
      console.error("Connection error:", error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("Max reconnection attempts reached");
      }
    });

    this.socket.on("connected", (data: any) => {
      console.log("Server confirmed connection:", data);
    });
  }

  /**
   * Register event handlers
   * Note: Removes existing listeners first to prevent duplicates (important for React StrictMode)
   */
  on(handlers: SocketEventHandlers): void {
    if (!this.socket) {
      console.error("Socket not initialized. Call connect() first.");
      return;
    }

    // Remove existing listeners first to prevent duplicates (critical for React StrictMode)
    this.socket.off("connect");
    this.socket.off("disconnect");
    this.socket.off("message_chunk");
    this.socket.off("message_complete");
    this.socket.off("status_update");
    this.socket.off("tool_execution");
    this.socket.off("error");
    this.socket.off("typing_status");
    this.socket.off("step_progress");
    this.socket.off("visualization_update");
    this.socket.off("request_user_input");

    if (handlers.onConnect) {
      this.socket.on("connect", handlers.onConnect);
    }

    if (handlers.onDisconnect) {
      this.socket.on("disconnect", handlers.onDisconnect);
    }

    if (handlers.onMessageChunk) {
      this.socket.on("message_chunk", handlers.onMessageChunk);
    }

    if (handlers.onMessageComplete) {
      this.socket.on("message_complete", handlers.onMessageComplete);
    }

    if (handlers.onStatusUpdate) {
      this.socket.on("status_update", handlers.onStatusUpdate);
    }

    if (handlers.onToolExecution) {
      this.socket.on("tool_execution", handlers.onToolExecution);
    }

    if (handlers.onError) {
      this.socket.on("error", handlers.onError);
    }

    if (handlers.onTyping) {
      this.socket.on("typing_status", handlers.onTyping);
    }

    // Performance Agent visualization events
    if (handlers.onVisualizationUpdate) {
      this.socket.on('visualization_update', handlers.onVisualizationUpdate);
    }

    if (handlers.onStepProgress) {
      this.socket.on('step_progress', handlers.onStepProgress);
    }

    // Interactive workflow event
    if (handlers.onRequestUserInput) {
      this.socket.on('request_user_input', handlers.onRequestUserInput);
    }
  }

  /**
   * Send chat message to server
   */
  sendMessage(message: string, context?: Record<string, any>): void {
    if (!this.socket?.connected) {
      console.error("Socket not connected");
      return;
    }

    const chatMessage: ChatMessage = {
      message,
      session_id: this.sessionId!,
      user_id: this.userId!,
      tenant_id: this.tenantId!,
      message_id: uuidv4(),
      context: context || {},
    };

    console.log("📤 Sending message:", chatMessage);
    this.socket.emit("chat_message", chatMessage);
  }

  /**
   * Send typing indicator
   */
  sendTyping(isTyping: boolean): void {
    if (!this.socket?.connected) return;

    this.socket.emit("typing", {
      session_id: this.sessionId,
      user_id: this.userId,
      is_typing: isTyping,
    });
  }

  /**
   * Send user response for interactive workflow
   */
  sendUserResponse(data: {
    session_id: string;
    step: number;
    input_type: string;
    value: any;
  }): void {
    if (!this.socket?.connected) {
      console.error("Socket not connected");
      return;
    }

    console.log("📤 Sending user response:", data);
    this.socket.emit("user_response", data);
  }

  /**
   * Create new session
   */
  createNewSession(): void {
    this.sessionId = uuidv4();
    console.log("New session created:", this.sessionId);
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Disconnect socket
   */
  disconnect(): void {
    if (this.socket) {
      console.log("Disconnecting socket...");
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Remove event listeners
   */
  off(event: string, handler?: Function): void {
    if (this.socket) {
      if (handler) {
        this.socket.off(event, handler as any);
      } else {
        this.socket.off(event);
      }
    }
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
